/**
 * Refund Log export — the pure row builders behind the CSV / Excel / PDF file, kept apart from the
 * table so the header, the rows and the Total row can be checked to line up column for column.
 *
 * The file mirrors the on-screen columns (UI label = export header) plus one extra: **Order date**,
 * straight after **Refund date**. On screen the order date is only a small caption under the refund
 * date, and only when it differs; in a file it needs a column of its own so the two dates can be
 * filtered and sorted. The Total row stays the last row.
 */
import { formatMoneyForExport } from '../../../utils/currency';
import type { ExportCell } from '../../../utils/reportExport';
import {
    REFUND_TYPE_LABEL,
    itemRefundAmount,
    refundTotal,
    taxRefundAmount,
    tipsAndGratuityRefundAmount,
    type RefundRecord,
} from './refundModel';
import type { RefundLogTotals } from './useRefundLogTableQuery';

export const REFUND_DATE_HEADER = 'Refund date';
export const ORDER_DATE_HEADER = 'Order date';

/** The on-screen column labels, with 'Order date' slotted in right after 'Refund date'. */
export function refundLogExportHeaders(columnLabels: string[]): string[] {
    const at = columnLabels.indexOf(REFUND_DATE_HEADER);
    if (at < 0) return [...columnLabels];
    return [...columnLabels.slice(0, at + 1), ORDER_DATE_HEADER, ...columnLabels.slice(at + 1)];
}

/** One refund event → one row, in `refundLogExportHeaders` order. */
export function refundLogExportRow(r: RefundRecord, scEnabled: boolean): ExportCell[] {
    const c = r.components;
    return [
        `#${r.orderNo}`,
        r.refundedAt,
        r.orderDate,
        REFUND_TYPE_LABEL[r.type],
        r.reason,
        r.staffName,
        formatMoneyForExport(itemRefundAmount(c)),
        formatMoneyForExport(taxRefundAmount(c)),
        formatMoneyForExport(tipsAndGratuityRefundAmount(c)),
        formatMoneyForExport(c.convenienceFee),
        formatMoneyForExport(c.deliveryFee),
        ...(scEnabled ? [formatMoneyForExport(c.serviceCharge)] : []),
        formatMoneyForExport(c.cardProcessingFee),
        formatMoneyForExport(refundTotal(c)),
    ];
}

/**
 * The Total row — straight off the period-wide `RefundLogTotals` the API reports at the response
 * root (the same figures the on-screen footer reads). Label first, then blanks over the text columns
 * (Refund date, Order date, Type, Reason, Staff), then the amounts.
 */
export function refundLogExportTotalsRow(count: number, t: RefundLogTotals, scEnabled: boolean): ExportCell[] {
    return [
        `Total (${count} refund${count === 1 ? '' : 's'})`, '', '', '', '', '',
        formatMoneyForExport(t.item),
        formatMoneyForExport(t.tax),
        formatMoneyForExport(t.tipsAndGratuity),
        formatMoneyForExport(t.convenienceFee),
        formatMoneyForExport(t.deliveryFee),
        ...(scEnabled ? [formatMoneyForExport(t.serviceCharge)] : []),
        formatMoneyForExport(t.cardProcessingFee),
        formatMoneyForExport(t.refunded),
    ];
}
